/**
 * Images on a bug report / feature request (#932): the rules both reporter
 * dialogs share, and the page capture.
 *
 * The server re-checks every one of these (#931: `routes/feature_requests.py`
 * refuses the whole submission on a bad image), so this module is UX: it tells
 * the user which file broke which rule before anything is sent.
 *
 *   | rule  | value                    | why                                   |
 *   |-------|--------------------------|---------------------------------------|
 *   | type  | PNG or JPEG, by bytes    | never SVG; the filename is not trusted|
 *   | size  | 5 MB each (decoded)      | FEEDBACK_SCREENSHOT_POLICY            |
 *   | count | 5 per report             | FR_MAX_ATTACHMENTS                    |
 *
 * Attached files over 5 MB are refused, never re-encoded: the user's own file
 * is not changed behind their back. Captures and annotated images are made by
 * us, so `fitJpegUnderLimit` always brings those under the limit.
 */

export const REPORT_IMAGE_LIMITS = Object.freeze({
    maxCount: 5,
    maxBytes: 5 * 1024 * 1024,
});

const JPEG_QUALITIES = [0.9, 0.8, 0.7, 0.6];
const CAPTURE_TIMEOUT_MS = 15000;

let lastId = 0;
/** A stable id for an image in the list (React key, replace-on-annotate). */
export function newImageId() {
    lastId += 1;
    return `img-${Date.now().toString(36)}-${lastId}`;
}

/** 'png' | 'jpeg' | null from a file's first bytes. */
export function sniffImageType(bytes) {
    const b = bytes || [];
    if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47
        && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return 'png';
    if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
    return null;
}

/** Decoded byte length of a base64 `data:` URL (what the server measures). */
export function dataUrlBytes(dataUrl) {
    const comma = typeof dataUrl === 'string' ? dataUrl.indexOf(',') : -1;
    if (comma < 0) return 0;
    const b64 = dataUrl.slice(comma + 1);
    const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}

export function formatMegabytes(bytes) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A refused file's size, rounded up so one just over the limit never reads as the limit. */
function formatOverLimit(bytes) {
    return `${(Math.ceil((bytes / (1024 * 1024)) * 10) / 10).toFixed(1)} MB`;
}

// FileReader rather than Blob#arrayBuffer: the jsdom that CRA's Jest ships
// predates Blob#arrayBuffer.
function readAs(blob, method) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('read_failed'));
        reader[method](blob);
    });
}

/**
 * Check one file and read it.
 * @returns {Promise<{ok: true, dataUrl: string} | {ok: false, message: string}>}
 */
export async function readImageFile(file, { maxBytes = REPORT_IMAGE_LIMITS.maxBytes } = {}) {
    const name = file?.name || 'This file';
    const head = new Uint8Array(await readAs(file.slice(0, 8), 'readAsArrayBuffer'));
    const type = sniffImageType(head);
    if (!type) {
        return { ok: false, message: `${name}: PNG or JPEG only. This file isn't a PNG or JPEG image.` };
    }
    if (file.size > maxBytes) {
        const limit = `${Number((maxBytes / (1024 * 1024)).toFixed(1))} MB`;
        return { ok: false, message: `${name}: over ${limit} (${formatOverLimit(file.size)}). Attach a smaller image.` };
    }
    // The data URL's type comes from the bytes: readAsDataURL takes it from the
    // filename, so a PNG named .svg would otherwise be labelled image/svg+xml.
    const dataUrl = await readAs(file, 'readAsDataURL');
    return { ok: true, dataUrl: dataUrl.replace(/^data:[^;,]*/, `data:image/${type}`) };
}

/** The one line for files that did not fit. */
export function overflowMessage(count, limits = REPORT_IMAGE_LIMITS) {
    return `Up to ${limits.maxCount} images. ${count} not added.`;
}

/**
 * Add files to a list that already holds `currentCount` images.
 * Files are taken in order until the list is full; one summary line covers
 * the files that did not fit (`overflow` of them).
 * @returns {Promise<{added: Array<{id, dataUrl, source, name}>, messages: string[], overflow: number}>}
 */
export async function addImageFiles(files, currentCount, limits = REPORT_IMAGE_LIMITS) {
    const added = [];
    const messages = [];
    let overflow = 0;
    for (const file of Array.from(files || [])) {
        if (currentCount + added.length >= limits.maxCount) {
            overflow += 1;
            continue;
        }
        // eslint-disable-next-line no-await-in-loop -- order matters, and at most 5 files are read
        const result = await readImageFile(file, limits);
        if (result.ok) {
            added.push({ id: newImageId(), dataUrl: result.dataUrl, source: 'upload', name: file.name });
        } else {
            messages.push(result.message);
        }
    }
    if (overflow) messages.push(overflowMessage(overflow, limits));
    return { added, messages, overflow };
}

// Open report dialogs. The floating Report button hides while another report
// dialog is open (New Request), also while that dialog steps aside for a
// capture; otherwise a second capture and a second dialog could start over it.
let openReports = 0;
const openListeners = new Set();
const notifyOpenReports = () => openListeners.forEach((listener) => listener());

/** Count a report dialog as open until the returned function is called. */
export function markReportOpen() {
    openReports += 1;
    notifyOpenReports();
    let closed = false;
    return () => {
        if (closed) return;
        closed = true;
        openReports -= 1;
        notifyOpenReports();
    };
}

export function subscribeOpenReports(listener) {
    openListeners.add(listener);
    return () => openListeners.delete(listener);
}

export function openReportCount() {
    return openReports;
}

/**
 * JPEG under the byte limit: step the quality down, then halve the size and
 * try again. Used for page captures and annotated images.
 */
export function fitJpegUnderLimit(canvas, maxBytes = REPORT_IMAGE_LIMITS.maxBytes) {
    let source = canvas;
    let last = null;
    for (let round = 0; round < 6; round += 1) {
        for (const quality of JPEG_QUALITIES) {
            last = source.toDataURL('image/jpeg', quality);
            if (dataUrlBytes(last) <= maxBytes) return last;
        }
        if (source.width < 400) break;
        const half = document.createElement('canvas');
        half.width = Math.round(source.width / 2);
        half.height = Math.round(source.height / 2);
        half.getContext('2d').drawImage(source, 0, 0, half.width, half.height);
        source = half;
    }
    return last;
}

/**
 * What a page capture leaves out: the report dialog and the annotation editor
 * (marked `data-report-ui`), the floating Report button
 * (`data-screenshot-capture`), iframes, which taint the canvas, and
 * `<noscript>`, whose "You need to enable JavaScript" fallback html2canvas
 * paints over the top-left corner although the browser never shows it.
 */
export function isExcludedFromCapture(el) {
    if (!el || !el.hasAttribute) return false;
    if (el.hasAttribute('data-report-ui') || el.hasAttribute('data-screenshot-capture')) return true;
    return el.tagName === 'IFRAME' || el.tagName === 'NOSCRIPT';
}

class CaptureTimeoutError extends Error {
    constructor() {
        super('capture_timeout');
        this.name = 'CaptureTimeoutError';
    }
}

/**
 * Capture the page as a JPEG data URL under the size limit.
 * html2canvas-pro is imported on first use so it stays out of the main bundle.
 * It replaces html2canvas 1.4.1, which throws on the `color(srgb …)` values
 * browsers resolve `color-mix()` / `oklch()` to (#932 Step 0).
 */
export async function capturePage({ timeoutMs = CAPTURE_TIMEOUT_MS } = {}) {
    // The timer starts before the import, so a chunk that never loads also ends
    // in a reason instead of a dialog that stays hidden.
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new CaptureTimeoutError()), timeoutMs);
    });
    try {
        const canvas = await Promise.race([
            import('html2canvas-pro').then(({ default: html2canvas }) => html2canvas(document.body, {
                useCORS: true,
                scale: 1,
                logging: false,
                backgroundColor: '#FAFAF5',
                ignoreElements: isExcludedFromCapture,
            })),
            timeout,
        ]);
        return fitJpegUnderLimit(canvas);
    } finally {
        clearTimeout(timer);
    }
}

/** A short reason for a failed capture, shown in the dialog. */
export function describeCaptureError(err) {
    const text = `${err?.name || ''} ${err?.message || ''}`;
    if (err instanceof CaptureTimeoutError || /timeout/i.test(text)) {
        return 'capturing took too long';
    }
    if (/colou?r/i.test(text)) {
        return "the page uses a colour style the capture tool can't read";
    }
    if (/taint|security|cross-origin|insecure/i.test(text)) {
        return "part of the page comes from another site and can't be captured";
    }
    return 'something went wrong while capturing';
}
